
import { useConfig } from "../contexts/ConfigContext"
import { Col, Form, Input, InputNumber, message, Modal, Row, Select, Spin } from "antd"
import React, { useEffect } from "react"
import dayjs from "dayjs"
import { useRegister } from "../contexts/RegisterContext"
import { modules } from "../config/pages"
import { useCompany } from "../contexts/CompanyContext"
import { useUser } from "../contexts/UserContext"
import useAuth from "../hooks/useAuth"
import { useStoreFetch } from "../hooks/useInventory"
import { useStore } from "../contexts/StoreContext"
import RegisterMovementEntryForm from "./forms/RegisterMovementEntryForm"
import RegisterMovementExitForm from "./forms/RegisterMovementExitForm"
import { useSecureRequest } from "../hooks/useSecureRequest"
import { movementStatus } from "../config/const"
import { TPayment } from "../models/storageModel"
import { mutate } from "swr"
const { Option } = Select

function CreateMovementModal(){
  const { config } = useConfig()
  const {user} = useUser()
  const {register,updateRegister} = useRegister()
  const {company} = useCompany()
  const {updateStore} = useStore()
  const { securePost } = useSecureRequest();
  const {
    pendingListEntries,
    pendingListExits,
    currentPending,
  } = register
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const mod = config.module
  const movementTypes = mod === modules.RegisterEntry ? register.movementTypesEntry : register.movementTypesExit
  const {tokenData} = useAuth.useToken('authCsrfToken',config.session,config.key)
  const {data} = useStoreFetch(tokenData,{
    all:true,
    company:company.current?.uid
  })
  useEffect(()=>{
    form.setFieldsValue({
      ...currentPending,
      created_by_user_document:user.document_number,
      created_by_user_id:user.id,
      created_by_user_name:user.name,
      document_type: register.currentMovementType,
    })
    if(data?.storehouse){
      updateStore({list: data.storehouse})
    }
  },[data,register,form,mod,pendingListEntries,pendingListExits])

  const getPayments = (values: any) => {
    const aux:Partial<TPayment>[] = []
    Object.keys(values).forEach(key => {
      if(key.startsWith("amount_") || key.startsWith("pay_method_" )){
        const paymentId = key.split("_").pop()! || "-1"
        const id = Number(paymentId)
        const index = aux.findIndex((item) => item.id === id)
        if(index === -1){
          const payMethodValue = values[`pay_method_${paymentId}`]
          const payModuleOption = register.paymentOptions.find((item) => item.key === payMethodValue)
          const amount = Number(values[`amount_${paymentId}`] || 0)
          if (amount > 0) {
            const paymentDateStr = values[`payment_date_${paymentId}`]
              ? dayjs(values[`payment_date_${paymentId}`]).startOf('minute').format()
              : (values.document_date ? dayjs(values.document_date).startOf('minute').format() : dayjs().startOf('minute').format());
            aux.push({
              id: id > 0 ? id : undefined,
              document_id: currentPending.id,
              payment_method: payModuleOption?.key || "CASH",
              payment_method_code: payModuleOption?.legal_code || "10",
              amount: amount,
              reference: values[`reference_${paymentId}`],
              payment_date: paymentDateStr
            })
          }
          delete values[`amount_${paymentId}`]
          delete values[`pay_method_${paymentId}`]
          delete values[`reference_${paymentId}`]
        }
      }
    })
    return {payments:aux,formValues:values}
  }
  const onFinishForm = async (values: any) => {
      updateRegister({showProgress:true})
      const isElectronicInvoicingEnabled = company.moduleOptions?.some(
        opt => (opt.key === "FACTUS_ENABLED" || opt.type === "ELECTRONIC_INVOICING") && (opt.show || (opt as any).is_active_for_company)
      ) || false;

      const emitAsElectronic = isElectronicInvoicingEnabled && values.is_electronic_invoice !== false && values.document_type === "SALE_INVOICE";

      //Al actualizar una orden, siempre se debe enviar sin los campos principales///
      const {created_at,updated_at,id,...newMovement} = currentPending
      newMovement.item_list = currentPending.item_list
      newMovement.company_id = newMovement.company_id || company.current?.id
      newMovement.status = movementStatus.APPROVED
      newMovement.invoice_type = emitAsElectronic ? "ELECTRONIC_INVOICE" : "SIMPLE_RECEIPT"
      newMovement.created_by_user_id = user.id
      newMovement.approved_by_user_id = values.approved_by_user_id || newMovement.approved_by_user_id || user.id
      newMovement.approved_by_user_name = values.approved_by_user_name || newMovement.approved_by_user_name || user.name
      newMovement.approved_by_user_document = values.approved_by_user_document || newMovement.approved_by_user_document || user.document_number
      newMovement.contact_name = values.contact_name || newMovement.contact_name || currentPending?.contact_name || ""
      newMovement.electronic_invoice_send_email = emitAsElectronic ? (values.send_email !== false) : false
      newMovement.contact_email = values.customer_email || ""

      const docType = values.document_type || newMovement.document_type || register.currentMovementType || ""
      const isPreparatory = ["PURCHASE_ORDER", "PURCHASE_REQUEST", "QUOTATION"].includes(docType)

      const executionMode = isPreparatory ? "IMMEDIATE" : (values.execution_mode || "IMMEDIATE")
      const isCredit = !isPreparatory && values.payment_term === "CREDIT"
      const creditDays = Number(values.credit_days) || 30
      const creditDueDate = isCredit ? dayjs().add(creditDays, 'day').format('YYYY-MM-DD') : (currentPending?.credit_due_date || '')

      const totalDoc = Number(currentPending.total_amount_net || values.total_amount_net || 0)
      const { payments, formValues } = getPayments(values)
      const totalPaid = isPreparatory ? 0 : payments.reduce((acc, p) => acc + (Number(p.amount) || 0), 0)
      const balanceDue = isCredit 
        ? Math.max(0, Math.round(((totalDoc - totalPaid) + Number.EPSILON) * 100) / 100)
        : 0

      newMovement.payment_term = isCredit ? "CREDIT" : "CASH"
      newMovement.execution_mode = executionMode
      newMovement.credit_days = isCredit ? creditDays : 0
      newMovement.credit_due_date = creditDueDate
      newMovement.total_paid_amount = totalPaid
      newMovement.balance_due_amount = balanceDue

      if (isPreparatory) {
        newMovement.payment_status = "UNPAID"
        newMovement.reception_status = "PENDING"
        newMovement.settlement_status = "OPEN"
      } else if (isCredit) {
        if (balanceDue <= 0 && totalPaid >= totalDoc) {
          newMovement.payment_status = "PAID"
        } else if (totalPaid > 0) {
          newMovement.payment_status = "PARTIALLY_PAID"
        } else {
          newMovement.payment_status = "UNPAID"
        }
        newMovement.reception_status = executionMode === "DEFERRED" ? "PENDING" : "RECEIVED"
        newMovement.settlement_status = (newMovement.reception_status === "RECEIVED" && newMovement.payment_status === "PAID") ? "SETTLED" : "OPEN"
      } else {
        newMovement.payment_status = "PAID"
        newMovement.reception_status = executionMode === "DEFERRED" ? "PENDING" : "RECEIVED"
        newMovement.settlement_status = (newMovement.reception_status === "RECEIVED" && newMovement.payment_status === "PAID") ? "SETTLED" : "OPEN"
      }

      const documentDateFormatted = values.document_date
        ? dayjs(values.document_date).startOf('minute').format()
        : (currentPending?.document_date ? dayjs(currentPending.document_date).startOf('minute').format() : dayjs().startOf('minute').format());
      newMovement.document_date = documentDateFormatted;
      formValues.document_date = documentDateFormatted;
      const attachments = formValues.attachments !== undefined ? formValues.attachments : (currentPending?.attachments || [])
      const data ={
        movement: {...formValues, ...newMovement, payments, attachments},
        company:company.current?.uid,
        uidMovement: currentPending.uid,
        module: config.module,
        typeMovement: mod === modules.RegisterEntry ? modules.RegisterEntry : modules.RegisterExit
      } 
      const path = '/inventory/movement/update-movement'
      const movementResponse = await securePost(path,data)
      if(movementResponse.status === 200){
        if(mod === modules.RegisterEntry){
          updateRegister({movementList:[],pendingListEntries:[],itemListEntries:[]})
        }
        if(mod === modules.RegisterExit){
          updateRegister({movementList:[],pendingListExits:[],itemListExits:[]})
        }
        updateRegister({showCreateMovement:false,currentPending:undefined})
        form.resetFields()
        mutate(
          (key: any) =>
            (typeof key === 'string' && key.includes('/inventory/movement/')) ||
            (Array.isArray(key) && typeof key[0] === 'string' && key[0].includes('/inventory/movement/')),
          undefined,
          { revalidate: true }
        )
        success()
      }else{
        fail('Por favor intente nuevamente')
      }
    updateRegister({showProgress:false})
    console.log("Created movement",{...currentPending,...values})
  }

  //Cambiar mensajes de error por modales, ya que este contexto se elimina y no se alcanza a ver

  function success(){
    messageApi.open({
      type: 'success',
      content: 'Registro creado con éxito',
    });
    return true
  };

  function fail(message:string){
    messageApi.open({
      type: 'error',
      content: message,
    });
    return true
  };

  const onCancel = () => {
    updateRegister({showCreateMovement:false})
    form.resetFields()
  }

  const onChange = (value:any) => {
    if(mod === modules.RegisterEntry){
      form.setFieldValue("contact_id","")
    }
  }
  const orderType = Form.useWatch("document_type",form)
  return (
    <Modal 
      title={<div style={{ textAlign: "center" }}>Datos de registro</div>} 
      open={register.showCreateMovement}
      maskClosable={false}
      onCancel={onCancel}
      confirmLoading={register.showProgress}
      okText="Confirmar"
      cancelText="Cancelar"
      okButtonProps={{htmlType:"submit",form:"create-movement"}}
      className="modal-responsive-md"
      destroyOnHidden
    >
      <Spin size="large" tip="Procesando..." fullscreen spinning={register.showProgress} />
      {contextHolder}
      <Form
        form={form}
        layout="vertical"
        name="create-movement"
        scrollToFirstError={{ block: 'end', focus: true }}
        style={{ maxWidth: '800px', margin: '0 auto' }}
        onFinish={onFinishForm}
      >   
        <Form.Item name="created_by_user_id" hidden><Input type="hidden" /></Form.Item>
        <Form.Item name="created_by_user_name" hidden><Input type="hidden" /></Form.Item>
        <Row gutter={[10, 10]} align="middle">
          <Col xs={24} sm={{span:12}}>
            <Form.Item
              label={orderType === "QUOTATION" ? "Elaborado por (Asesor)" : orderType === "SALE_INVOICE" ? "Vendedor" : "Solicitante"}
              name={`created_by_user_document`}
              rules={[{ required: true, message: ''}]}
            >
              <InputNumber disabled maxLength={15} min={0} placeholder={orderType === "QUOTATION" ? "# documento asesor" : orderType === "SALE_INVOICE" ? "# documento vendedor" : "# documento solicitante"} style={{ width: '100%' }}></InputNumber>
            </Form.Item>
          </Col>
          <Col xs={24} sm={{span:12}}>
            <Form.Item
              label="Tipo de orden"
              name={`document_type`}
              rules={[{ required: true, message: ''}]}
            >
              <Select placeholder="Selecciona tipo de orden" onChange={onChange} disabled>
                {movementTypes.map(el =>(
                  <Option key={el.key} value={el.key}>{el.string_value}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>     
       {mod === modules.RegisterEntry && orderType && <RegisterMovementEntryForm form={form}/>}
       {mod === modules.RegisterExit && orderType && <RegisterMovementExitForm form={form}/>}
      </Form>
    </Modal>
  )
}

export default CreateMovementModal